import React, { ReactElement, useRef } from 'react';
import {
  MapClusterer,
  MapPinView,
  PinCluster,
} from '@hotelplan/components.common.map-pin';
import { BsIcon } from '@hotelplan/core.basis.bs-icon';
import { FdrMapProvider } from '@hotelplan/fdr.regular.fusion.fdr-map';
import { useObjectId } from '@hotelplan/libs.context.object-id';
import { useElementSize } from '@hotelplan/libs.hooks-dom';
import { FdrAgency } from '@hotelplan/supergraph-api';
import { commonFdrMapOptions } from 'components/domain/fdr-map';
import { FdrAgencyMapPins } from './fdr-agency-map-pins';
import {
  ClusterText,
  FdrAgenciesMapWrapper,
  MapCluster,
} from './fdr-agency-map.styles';

const DEFAULT_MAP_ZOOM = 12;

function renderPinContent(cluster?: PinCluster, id?: string): ReactElement {
  const clusterSize = cluster?.getSize();

  const includeSelectedPin = cluster.markers.some(
    marker => (marker as MapPinView).object.id === id
  );

  return (
    <MapCluster>
      <BsIcon
        size="xxlg"
        name={includeSelectedPin ? 'cluster-icon-selected' : 'cluster-icon'}
      />
      <ClusterText>{clusterSize}</ClusterText>
    </MapCluster>
  );
}

interface IFdrAgencyMap {
  agencies?: Array<FdrAgency>;
  fitBounds?: boolean;
  setMap?: (map: google.maps.Map) => void;
  initialMapZoom?: number;
}

export function FdrAgencyMap({
  agencies,
  fitBounds,
  setMap,
  initialMapZoom = DEFAULT_MAP_ZOOM,
}: IFdrAgencyMap): ReactElement {
  const agencyMapWrapperRef = useRef<HTMLDivElement>(undefined);
  const { id } = useObjectId();

  const mapSize = useElementSize(agencyMapWrapperRef, { enable: true });

  return (
    <>
      <FdrAgenciesMapWrapper ref={agencyMapWrapperRef}>
        <FdrMapProvider
          onLoad={setMap}
          styles={{
            width: `${mapSize.w}px`,
            height: `${mapSize.h}px`,
          }}
          options={commonFdrMapOptions}
          zoom={initialMapZoom}
        >
          <MapClusterer
            renderPinContent={cluster => renderPinContent(cluster, id)}
            className="agency-cluster"
          >
            <FdrAgencyMapPins
              agencies={agencies}
              selectedId={id}
              fitBounds={fitBounds}
            />
          </MapClusterer>
        </FdrMapProvider>
      </FdrAgenciesMapWrapper>
    </>
  );
}
